import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import { Col, Row } from 'react-flexbox-grid';
import { saveAs } from 'file-saver';

import { LOGGER_LOG_TYPE } from 'Config';

import Box from 'common/components/box/Box';
import Label from 'common/components/label/Label';
import Button from 'common/components/button/Button';
import FormItem from 'common/components/formItem/FormItem';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import FileSelector from 'common/components/fileSelector/FileSelector';
import { CompactPageLayout } from 'common/components/compactPageLayout';
import Logger from 'common/services/Logger';
import Loading from 'common/services/Loading';
import CustomFile from 'common/models/CustomFile';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import ImportDataService from 'api/import-data/ImportDataService';
import GenericTypesService from 'api/genericTypes/GenericTypesService';
import MaintenanceContractTypesService from 'api/maintenanceContractTypes/MaintenanceContractTypesService';
import InsuranceContractsService from 'api/contracts/insuranceContract/InsuranceContractsService';
import VehicleSegmentsService from 'api/vehicleSegments/VehicleSegmentsService';
import VehicleBrandsService from 'api/vehicleBrands/VehicleBrandsService';
import VehiclesService from 'api/vehicles/VehiclesService';
import RolesService from 'api/roles/RolesService';
import LanguagesService from 'api/languages/LanguagesService';
import { ImportDataParseResult } from 'api/import-data/models/ImportDataParseResult';
import ImportPreview from './components/ImportPreview';
import styles from './ImportDataScreen.module.scss';
import moment from 'moment';
import { useHistory } from 'react-router-dom';

type ScreenStep = 'upload' | 'preview';

const ImportDataScreen: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { addToast } = useToasts();
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasUserImportDataWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_IMPORT_DATA_WRITE']);
    const history = useHistory();

    const [step, setStep] = useState<ScreenStep>('upload');
    const [parsedData, setParsedData] = useState<ImportDataParseResult | null>(null);
    const [isValidating, setIsValidating] = useState(false);
    const [expenseTypes, setExpenseTypes] = useState<string[]>([]);
    const [maintenanceTypes, setMaintenanceTypes] = useState<string[]>([]);
    const [insuranceTypes, setInsuranceTypes] = useState<string[]>([]);
    const [segments, setSegments] = useState<string[]>([]);
    const [brands, setBrands] = useState<string[]>([]);
    const [roles, setRoles] = useState<string[]>([]);
    const [languages, setLanguages] = useState<string[]>([]);
    const [registrations, setRegistrations] = useState<string[]>([]);

    // Load types on mount
    useEffect(() => {
        const loadExpenseTypes = async () => {
            try {
                const types = await GenericTypesService.catalogSupplier();
                // Translate system types, keep custom types as-is
                setExpenseTypes(types.map(type =>
                    type.isSystem ? t(`common.generic_types.${type.label}` as any) : (type.label || type.name)
                ));
            } catch (error) {
                Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't load expense types`, error);
            }
        };

        const loadMaintenanceTypes = async () => {
            try {
                const types = await MaintenanceContractTypesService.catalog();
                // Translate system types (enum keys like BODY_WORK), keep custom types as-is
                setMaintenanceTypes(types.map(type => {
                    const isSystemType = /^[A-Z_]+$/.test(type.label);
                    if (isSystemType) {
                        const translated = t(`common.maintenance_contract_type.${type.label}` as any);
                        // If translation exists (not the same as key), use it
                        return translated !== `common.maintenance_contract_type.${type.label}` ? translated : type.label;
                    }
                    return type.label;
                }));
            } catch (error) {
                Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't load maintenance types`, error);
            }
        };

        const loadInsuranceTypes = async () => {
            try {
                const types = await InsuranceContractsService.getInsuranceContractTypes();
                // Translate system types, keep custom types as-is
                setInsuranceTypes(types.map(type => {
                    if (type.isSystem) {
                        return t(`common.insurance_contract_types.${type.label}` as any);
                    }
                    return type.label;
                }));
            } catch (error) {
                Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't load insurance types`, error);
            }
        };

        const loadSegments = async () => {
            try {
                const segmentsData = await VehicleSegmentsService.catalog();
                setSegments(segmentsData.map(s => s.label).filter(Boolean).sort());
            } catch (error) {
                Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't load segments`, error);
            }
        };

        const loadBrands = async () => {
            try {
                const brandsData = await VehicleBrandsService.catalog();
                setBrands(brandsData.map(b => b.label).filter(Boolean).sort());
            } catch (error) {
                Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't load brands`, error);
            }
        };

        const loadRoles = async () => {
            try {
                const rolesData = await RolesService.getAllForCompany(userProfile?.companyId);
                // Translate system roles, keep custom roles as-is
                setRoles(rolesData.map(role => {
                    // First try to use the internalName if available for system roles
                    const nameToTranslate = role.internalName || role.name;
                    // Check if it looks like a system role (all uppercase with underscores)
                    const isSystemRole = /^[A-Z_]+$/.test(nameToTranslate);
                    if (isSystemRole) {
                        const translated = t(`common.roles.${nameToTranslate}` as any);
                        // If translation exists (not same as key), use it
                        return translated !== `common.roles.${nameToTranslate}` ? translated : role.name;
                    }
                    return role.name;
                }).filter(Boolean).sort());
            } catch (error) {
                Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't load roles`, error);
            }
        };

        const loadLanguages = async () => {
            try {
                const languagesData = await LanguagesService.GetAllForSelectItem();
                setLanguages(languagesData.map(l => l.fullLabel || l.label).filter(Boolean).sort());
            } catch (error) {
                Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't load languages`, error);
            }
        };

        const loadRegistrations = async () => {
            try {
                const vehiclesCatalog = await VehiclesService.catalog();
                setRegistrations((vehiclesCatalog || []).map(v => v.label).filter(Boolean).sort());
            } catch (error) {
                Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't load vehicles catalog`, error);
            }
        };

        loadExpenseTypes();
        loadMaintenanceTypes();
        loadInsuranceTypes();
        loadSegments();
        loadBrands();
        loadRoles();
        loadLanguages();
        loadRegistrations();
    }, [t, i18n.language, userProfile?.companyId]);

    useEffect(() => {
        if (!hasUserImportDataWritePolicy && userProfile && !userProfile.hasExcelImportData) {
            history.push('/');
        }
    }, [userProfile, history]);

    const downloadTemplate = async () => {
        try {
            Loading.show();
            const template = await ImportDataService.downloadTemplate();
            saveAs(template, `Import_Template_${moment(new Date()).format('YYYY_MM_DD_HH_mm_ss')}.xlsx`);
            Loading.hide();
        } catch (error) {
            Loading.hide();
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't download template`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }
    };

    const handleFileUpload = async (files: CustomFile[]) => {
        if (!files || files.length === 0) return;

        try {
            Loading.show();
            const result = await ImportDataService.parse(files[0]);
            setParsedData(result);
            setStep('preview');
            Loading.hide();
        } catch (error: any) {
            Loading.hide();
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't parse file`, error);

            if (error?.response?.data?.errorCode === 'OUTDATED_TEMPLATE') {
                addToast(t('import_data.outdated_template_error' as any), { appearance: 'error', autoDismiss: false });
                return;
            }

            addToast(t('import_data.parse_error'), { appearance: 'error' });
        }
    };

    const handleDataChange = useCallback(async (data: ImportDataParseResult) => {
        setParsedData(data);

        // Debounced validation
        setIsValidating(true);
        try {
            const validatedData = await ImportDataService.validate(data);
            setParsedData(validatedData);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't validate data`, error);
        } finally {
            setIsValidating(false);
        }
    }, []);

    const handleImport = async () => {
        if (!parsedData) return;

        if (!parsedData.isValid) {
            addToast(t('import_data.fix_errors_first'), { appearance: 'warning' });
            return;
        }

        try {
            Loading.show();
            const result = await ImportDataService.importAll(parsedData);
            Loading.hide();

            addToast(t('import_data.import_success'), { appearance: 'success' });

            // Reset to upload step
            setParsedData(null);
            setStep('upload');
        } catch (error: any) {
            Loading.hide();

            if (error?.response?.status === 402) {
                addToast(t('common.errors.pack_limit_exceeded'), { appearance: 'warning' });
            } else if (error?.response?.status === 422) {
                addToast(t('common.errors.empty_file'), { appearance: 'warning' });
            } else {
                Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't import data`, error);
                addToast(t('import_data.import_error'), { appearance: 'error' });
            }
        }
    };

    const handleCancel = () => {
        setParsedData(null);
        setStep('upload');
    };

    return (
        <ScreenTitle title={t('import_data.title')}>
            <CompactPageLayout
                title={t('import_data.title') as string}
                onBack={() => history.goBack()}
            >
                {step === 'upload' && (
                    <Box className={styles.box}>
                        <Row>
                            <Col xs={12}>
                                <div className={styles.uploadSection}>
                                    <div className={styles.instructions}>
                                        <h3>{t('import_data.instructions_title')}</h3>
                                        <ol>
                                            <li>{t('import_data.instructions_step1')}</li>
                                            <li>{t('import_data.instructions_step2')}</li>
                                            <li>{t('import_data.instructions_step3')}</li>
                                            <li>{t('import_data.instructions_step4')}</li>
                                        </ol>
                                    </div>

                                    <div className={styles.downloadSection}>
                                        <Button
                                            text={t('import_data.download_template')}
                                            onClick={downloadTemplate}
                                        />
                                    </div>

                                    <div className={styles.uploadArea}>
                                        <Label className={styles.label}>{t('import_data.upload_file')} (Excel):</Label>
                                        <FormItem>
                                            <FileSelector
                                                onFilesChanged={handleFileUpload}
                                                fileButtonSizes={{ sm: 12 }}
                                                label={<Label className={styles.label}>{t('import_data.select_file')}</Label>}
                                                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                            />
                                        </FormItem>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Box>
                )}

                {step === 'preview' && parsedData && (
                    <Box className={styles.box}>
                        <ImportPreview
                            data={parsedData}
                            onDataChange={handleDataChange}
                            existingExpenseTypes={expenseTypes}
                            existingMaintenanceTypes={maintenanceTypes}
                            existingInsuranceTypes={insuranceTypes}
                            existingSegments={segments}
                            existingBrands={brands}
                            existingRoles={roles}
                            existingLanguages={languages}
                            existingRegistrations={registrations}
                        />

                        <div className={styles.buttonsContainer}>
                            <Button
                                text={t('common.cancel')}
                                onClick={handleCancel}
                                preset="secondary"
                            />
                            <Button
                                text={isValidating ? t('import_data.validating') : t('import_data.import')}
                                onClick={handleImport}
                                isDisabled={!parsedData.isValid || isValidating}
                            />
                        </div>
                    </Box>
                )}
            </CompactPageLayout>
        </ScreenTitle>
    );
};

export default ImportDataScreen;
